/**
 * Google Places API Integration Service
 *
 * Handles communication with Google Places API (New) for address autocomplete
 * and place details retrieval.
 */

const PLACES_API_BASE = 'https://places.googleapis.com/v1'

/**
 * Search for places using autocomplete
 *
 * @param {string} query - Search query (typically business name)
 * @param {string} apiKey - Google Places API key
 * @returns {Promise<Array>} Array of place predictions
 */
export async function searchPlaces(query, apiKey) {
  if (!query || !query.trim()) {
    return []
  }

  if (!apiKey) {
    throw new Error('Google Places API key is not configured')
  }

  try {
    const response = await fetch(`${PLACES_API_BASE}/places:autocomplete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': apiKey
      },
      body: JSON.stringify({
        input: query,
        includedRegionCodes: ['GB'], // Focus on UK addresses
        languageCode: 'en-GB'
      })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error?.message || `API request failed: ${response.status}`)
    }

    const data = await response.json()
    return data.suggestions || []
  } catch (error) {
    console.error('[GooglePlaces] Search error:', error)
    throw error
  }
}

/**
 * Get detailed place information by place ID
 *
 * @param {string} placeId - Google Place ID
 * @param {string} apiKey - Google Places API key
 * @returns {Promise<Object>} Place details
 */
export async function getPlaceDetails(placeId, apiKey) {
  if (!placeId) {
    throw new Error('Place ID is required')
  }

  if (!apiKey) {
    throw new Error('Google Places API key is not configured')
  }

  try {
    const response = await fetch(`${PLACES_API_BASE}/places/${placeId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': apiKey,
        'X-Goog-FieldMask': 'id,displayName,formattedAddress,addressComponents,internationalPhoneNumber'
      }
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error?.message || `API request failed: ${response.status}`)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('[GooglePlaces] Details error:', error)
    throw error
  }
}

/**
 * Parse Google Places data into Odin's address format
 *
 * @param {Object} placeData - Place details from Google Places API
 * @returns {Object} Formatted address data matching Odin's structure
 */
export function parsePlaceToOdinFormat(placeData) {
  if (!placeData || !placeData.addressComponents) {
    return {
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      city: '',
      postcode: '',
      country: 'United Kingdom',
      phone: ''
    }
  }

  const components = placeData.addressComponents || []

  // Helper to find component by type
  const findComponent = (types) => {
    return components.find(comp =>
      comp.types?.some(type => types.includes(type))
    )
  }

  // Extract address components
  const streetNumber = findComponent(['street_number'])?.longText || ''
  const route = findComponent(['route'])?.longText || ''
  const subpremise = findComponent(['subpremise'])?.longText || ''
  const locality = findComponent(['locality', 'postal_town'])?.longText || ''
  const adminArea = findComponent(['administrative_area_level_2'])?.longText || ''
  const postcode = findComponent(['postal_code'])?.longText || ''
  const country = findComponent(['country'])?.longText || 'United Kingdom'

  // Build address line 1 (street number + route)
  let addressLine1 = ''
  if (streetNumber && route) {
    addressLine1 = `${streetNumber} ${route}`
  } else if (route) {
    addressLine1 = route
  } else if (streetNumber) {
    addressLine1 = streetNumber
  }

  // Address line 2 (subpremise like "Suite 100", "Unit 2", etc)
  const addressLine2 = subpremise || ''

  // City (prefer locality, fallback to admin area)
  const city = locality || adminArea || ''

  // Phone number (formatted)
  const phone = placeData.internationalPhoneNumber || ''

  return {
    addressLine1,
    addressLine2,
    addressLine3: '', // Always empty, let user fill if needed
    city,
    postcode,
    country,
    phone
  }
}

/**
 * Format place suggestion for display in dropdown
 *
 * @param {Object} suggestion - Place suggestion from autocomplete API
 * @returns {Object} Formatted display data
 */
export function formatSuggestionForDisplay(suggestion) {
  if (!suggestion || !suggestion.placePrediction) {
    return {
      id: '',
      primaryText: '',
      secondaryText: ''
    }
  }

  const prediction = suggestion.placePrediction
  const mainText = prediction.structuredFormat?.mainText?.text || prediction.text?.text || ''
  const secondaryText = prediction.structuredFormat?.secondaryText?.text || ''

  return {
    id: prediction.placeId,
    primaryText: mainText,
    secondaryText: secondaryText
  }
}

/**
 * Validate Google Places API key format
 *
 * @param {string} apiKey - API key to validate
 * @returns {boolean} Whether the key appears valid
 */
export function validateApiKey(apiKey) {
  if (!apiKey || typeof apiKey !== 'string') {
    return false
  }

  // Basic validation: should start with AIza and be reasonable length
  return apiKey.startsWith('AIza') && apiKey.length > 30
}
